/**
 * ValueService - Handles value management
 */

import { ValueRepository } from '../repositories/ValueRepository';

/**
 * Validate value data
 * @param {Object} valueData - Value data to validate
 * @returns {Object} Validation result { isValid, error }
 */
const validateValueData = (valueData) => {
  if (!valueData.name || typeof valueData.name !== 'string') {
    return { isValid: false, error: 'Value name is required and must be a string' };
  }

  if (valueData.name.trim().length === 0) {
    return { isValid: false, error: 'Value name cannot be empty' };
  }

  if (valueData.name.trim().length > 100) {
    return { isValid: false, error: 'Value name cannot exceed 100 characters' };
  }

  return { isValid: true };
};

export class ValueService {
  constructor(valueRepo = null) {
    this.valueRepo = valueRepo || new ValueRepository();
  }

  /**
   * Create a new value
   * @param {Object} valueData - Value data
   * @param {string} schoolId - School ID (required for hierarchical collections)
   * @returns {Promise<Object>} Created value
   */
  async createValue(valueData, schoolId) {
    try {
      // Validate value data
      const validation = validateValueData(valueData);
      if (!validation.isValid) {
        throw new Error(validation.error);
      }

      // Check for duplicates
      const existing = await this.valueRepo.getByName(valueData.name.trim(), schoolId);
      if (existing) {
        throw new Error(`Value "${valueData.name}" already exists`);
      }

      return await this.valueRepo.createValue(valueData);
    } catch (error) {
      console.error('Error creating value:', error);
      throw error;
    }
  }

  /**
   * Get all values
   * @param {string} schoolId - School ID (required for hierarchical collections)
   * @param {boolean} activeOnly - Only return active values
   * @returns {Promise<Array>} Array of values
   */
  async getValues(schoolId, activeOnly = true) {
    try {
      if (!schoolId) {
        console.warn('ValueService.getValues: schoolId required but not provided');
        return [];
      }

      if (activeOnly) {
        return await this.valueRepo.getActiveValues(schoolId);
      }
      return await this.valueRepo.getAllValues(schoolId);
    } catch (error) {
      console.error('Error fetching values:', error);
      return [];
    }
  }

  /**
   * Subscribe to value changes
   * @param {Function} callback - Callback for updates
   * @param {string} schoolId - School ID (required for hierarchical collections)
   * @param {boolean} activeOnly - Only subscribe to active values
   * @returns {Function} Unsubscribe function
   */
  subscribeValues(callback, schoolId, activeOnly = true) {
    // Guard: require schoolId for hierarchical collections
    if (!schoolId) {
      console.warn('ValueService.subscribeValues: schoolId required but not provided');
      callback([]);
      return () => {};
    }

    const constraints = [];

    if (activeOnly) {
      constraints.push({ field: 'active', operator: '==', value: true });
    }

    return this.valueRepo.subscribe(callback, constraints, schoolId);
  }

  /**
   * Update a value
   * @param {string} valueId - Value ID
   * @param {Object} updates - Updates to apply
   * @param {string} schoolId - School ID (required for hierarchical collections)
   * @returns {Promise<Object>} Updated value
   */
  async updateValue(valueId, updates, schoolId) {
    try {
      // Validate updates if changing name
      if (updates.name) {
        const validation = validateValueData({
          name: updates.name
        });

        if (!validation.isValid) {
          throw new Error(validation.error);
        }

        // Check for duplicates (excluding current value)
        const existing = await this.valueRepo.getByName(updates.name.trim(), schoolId);
        if (existing && existing.id !== valueId) {
          throw new Error(`Value "${updates.name}" already exists`);
        }
      }

      return await this.valueRepo.updateValue(valueId, updates);
    } catch (error) {
      console.error('Error updating value:', error);
      throw error;
    }
  }

  /**
   * Delete a value
   * @param {string} valueId - Value ID
   * @returns {Promise<void>}
   */
  async deleteValue(valueId, schoolId) {
    try {
      // Prefer deactivation over deletion
      return await this.valueRepo.deactivateValue(valueId, schoolId);
    } catch (error) {
      console.error('Error deleting value:', error);
      throw error;
    }
  }

  /**
   * Get value by ID
   * @param {string} valueId - Value ID
   * @returns {Promise<Object|null>} Value or null
   */
  async getValueById(valueId) {
    try {
      return await this.valueRepo.getById(valueId);
    } catch (error) {
      console.error('Error fetching value by ID:', error);
      return null;
    }
  }

  /**
   * Get value by name
   * @param {string} name - Value name
   * @param {string} schoolId - School ID (required for hierarchical collections)
   * @returns {Promise<Object|null>} Value or null
   */
  async getValueByName(name, schoolId) {
    try {
      return await this.valueRepo.getByName(name, schoolId);
    } catch (error) {
      console.error('Error fetching value by name:', error);
      return null;
    }
  }

  /**
   * Search values by name or description
   * @param {string} searchTerm - Search term
   * @param {string} schoolId - School ID (required for hierarchical collections)
   * @returns {Promise<Array>} Matching values
   */
  async searchValues(searchTerm, schoolId) {
    try {
      return await this.valueRepo.searchValues(searchTerm, schoolId);
    } catch (error) {
      console.error('Error searching values:', error);
      return [];
    }
  }

  /**
   * Get value statistics
   * @param {string} schoolId - School ID (required for hierarchical collections)
   * @returns {Promise<Object>} Statistics
   */
  async getValueStatistics(schoolId) {
    try {
      return await this.valueRepo.getValueStatistics(schoolId);
    } catch (error) {
      console.error('Error fetching value statistics:', error);
      return {
        totalValues: 0,
        activeValues: 0,
        inactiveValues: 0
      };
    }
  }

  /**
   * Import values from array
   * @param {Array} values - Array of value data
   * @param {string} schoolId - School ID (required for hierarchical collections)
   * @returns {Promise<Object>} Import results
   */
  async importValues(values, schoolId) {
    try {
      const results = {
        success: [],
        failed: [],
        duplicates: []
      };

      for (const value of values) {
        try {
          // Check for duplicates
          const existing = await this.getValueByName(value.name, schoolId);
          if (existing) {
            results.duplicates.push(value);
            continue;
          }

          const created = await this.createValue(value, schoolId);
          results.success.push(created);
        } catch (error) {
          results.failed.push({ value, error: error.message });
        }
      }

      return results;
    } catch (error) {
      console.error('Error importing values:', error);
      throw error;
    }
  }

  /**
   * Update sort orders for multiple values
   * @param {Array} updates - Array of {id, sortOrder} objects
   * @returns {Promise<void>}
   */
  async updateValueSortOrders(updates, schoolId) {
    try {
      return await this.valueRepo.updateValueSortOrders(updates, schoolId);
    } catch (error) {
      console.error('Error updating value sort orders:', error);
      throw error;
    }
  }
}
